/**
 * Created: 2026-09-13
 * Initial commit: Add device management page for app admin
 *
 * IoT device management page showing all devices across all sites.
 */

import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import AddIcon from '@mui/icons-material/Add'
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import { zhCN } from 'date-fns/locale'
import { useState } from 'react'
import type { IotDevicesQuery } from '../gql/graphql'
import AddIotDeviceDialog from './AddIotDeviceDialog'

const IOT_DEVICES_QUERY = gql`
  query IotDevices {
    iotDevices {
      deviceId
      site {
        id
        name
      }
      certSerial
      certExpiry
      lastSeen
      createdAt
    }
  }
`

export default function IotDevicesPage() {
  const { data, loading, error, refetch } = useQuery<IotDevicesQuery>(IOT_DEVICES_QUERY)
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">加载设备列表失败: {error.message}</Typography>
      </Box>
    )
  }

  const devices = data?.iotDevices || []

  const formatDateTime = (timestamp: string | null | undefined) => {
    if (!timestamp) return '-'
    return format(new Date(timestamp), 'yyyy-MM-dd HH:mm:ss', { locale: zhCN })
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4" gutterBottom>
          IoT 设备管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          添加设备
        </Button>
      </Box>

      <AddIotDeviceDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdded={() => refetch()}
      />

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>设备 ID</TableCell>
              <TableCell>场馆</TableCell>
              <TableCell>证书序列号</TableCell>
              <TableCell>证书过期时间</TableCell>
              <TableCell>最后在线时间</TableCell>
              <TableCell>创建时间</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">暂无设备</Typography>
                </TableCell>
              </TableRow>
            ) : (
              devices.map((device) => (
                <TableRow key={device.deviceId}>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                    >
                      {device.deviceId}
                    </Typography>
                  </TableCell>
                  <TableCell>{device.site.name}</TableCell>
                  <TableCell>
                    {device.certSerial ? (
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                      >
                        {device.certSerial}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{formatDateTime(device.certExpiry)}</TableCell>
                  <TableCell>{formatDateTime(device.lastSeen)}</TableCell>
                  <TableCell>{formatDateTime(device.createdAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
